/**
 * Breadcrumbs (ADR 0076): the levels below a page, which the top bar draws after the page's
 * own name. A page with a sub-view declares them once; the shell does the rest.
 *
 *   usePageCrumbs([{ label: 'Landing queue' }])                         Pipelines / Landing queue
 *   usePageCrumbs([{ label: 'Orgus', to: '/projects?project=p1' }, { label: 'main' }])
 *
 * The last crumb is the view on screen; earlier ones carry `to` and render as links.
 */
import { useEffect } from 'react';
import { create } from 'zustand';

export interface Crumb {
  label: string;
  /** Where the crumb leads. The last crumb (the current view) needs none. */
  to?: string;
}

const useCrumbStore = create<{ trail: readonly Crumb[] }>()(() => ({ trail: [] }));

/** Declare this page's levels below its own name. Pass [] (or nothing) for the page itself. */
export function usePageCrumbs(trail: readonly Crumb[] | null | undefined): void {
  const key = JSON.stringify(trail ?? []);
  useEffect(() => {
    const t = JSON.parse(key) as Crumb[];
    useCrumbStore.setState({ trail: t });
    return () => {
      if (useCrumbStore.getState().trail === t) useCrumbStore.setState({ trail: [] });
    };
  }, [key]);
}

/** The levels the current page declared. */
export function useCrumbs(): readonly Crumb[] {
  return useCrumbStore((s) => s.trail);
}

/** The name of the view on screen: its deepest crumb, else the page's own title. */
export function viewLabel(pageTitle: string, trail: readonly Crumb[]): string {
  return trail.length ? trail[trail.length - 1]!.label : pageTitle;
}
